// 이 선택적 코드는 서비스 워커를 등록하는 데 사용됩니다.
// register()는 기본적으로 호출되지 않습니다.

// 이를 통해 프로덕션에서 후속 방문 시 앱이 더 빠르게 로드되고,
// 오프라인 기능을 제공합니다. 하지만 이는 또한 개발자(및 사용자)가
// 페이지에 열린 모든 기존 탭이 닫힌 후 페이지의 후속 방문에서만
// 배포된 업데이트를 볼 수 있음을 의미합니다. 이전에 캐시된
// 리소스는 백그라운드에서 업데이트되기 때문입니다.

// 이 모델의 이점과 옵트인 방법에 대한 자세한 내용은
// https://cra.link/PWA 를 읽어보세요

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1]은 IPv6 localhost 주소입니다.
    window.location.hostname === "[::1]" ||
    // 127.0.0.0/8은 IPv4에서 localhost로 간주됩니다.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// iOS 감지 함수
const isIOS = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  // iOS에서는 Service Worker 등록을 건너뜀
  if (isIOS()) {
    console.log("iOS detected - Service Worker registration skipped");
    return;
  }

  if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
    // URL 생성자는 SW를 지원하는 모든 브라우저에서 사용할 수 있습니다.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // PUBLIC_URL이 페이지가 제공되는 곳과 다른 origin에 있으면
      // 서비스 워커가 작동하지 않을 것입니다. 이는 CDN이 자산을 제공하는 데
      // 사용되는 경우 발생할 수 있습니다; https://github.com/facebook/create-react-app/issues/2374 참조
      return;
    }

    window.addEventListener("load", () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // localhost에서 실행 중입니다. 서비스 워커가 여전히 존재하는지 확인해봅시다.
        checkValidServiceWorker(swUrl, config);

        // localhost에 추가 로깅을 추가하여 개발자를 서비스 워커/PWA 문서로 안내합니다.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            "This web app is being served cache-first by a service " +
              "worker. To learn more, visit https://cra.link/PWA"
          );
        });
      } else {
        // localhost가 아닙니다. 서비스 워커를 등록하기만 하면 됩니다
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  if (isIOS()) return; // iOS에서는 등록하지 않음

  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // 이 시점에서 업데이트된 사전 캐시된 콘텐츠가 가져와졌지만,
              // 모든 클라이언트 탭이 닫힐 때까지 이전 서비스 워커가
              // 여전히 이전 콘텐츠를 제공할 것입니다.
              console.log(
                "New content is available and will be used when all " +
                  "tabs for this page are closed. See https://cra.link/PWA."
              );

              // 콜백 실행
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // 이 시점에서 모든 것이 사전 캐시되었습니다.
              // "콘텐츠가 오프라인 사용을 위해 캐시되었습니다." 메시지를
              // 표시하기에 완벽한 시점입니다.
              console.log("Content is cached for offline use.");

              // 콜백 실행
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error("Error during service worker registration:", error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  if (isIOS()) return; // iOS에서는 체크하지 않음

  // 서비스 워커를 찾을 수 있는지 확인합니다. 찾을 수 없으면 페이지를 새로고침합니다.
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      // 서비스 워커가 존재하는지, 그리고 실제로 JS 파일을 가져오고 있는지 확인합니다.
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        // 서비스 워커를 찾을 수 없습니다. 아마도 다른 앱일 것입니다. 페이지를 새로고침합니다.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // 서비스 워커를 찾았습니다. 정상적으로 진행합니다.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        "No internet connection found. App is running in offline mode."
      );
    });
}

export function unregister() {
  if (isIOS()) {
    console.log("iOS detected - Service Worker unregistration skipped");
    return;
  }

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
